import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmt } from "../lib/api";
import Nav from "../components/Nav";
import { toast } from "sonner";
import { Plus, X, MagnifyingGlass, TrendUp, TrendDown, ArrowRight } from "@phosphor-icons/react";

function MarginPill({ label, value, color }) {
  const v = value ?? 0;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-3 h-3 inline-block" style={{ background: color }} />
      <span className="text-ink2">{label}</span>
      <span>{v.toFixed(1)}%</span>
    </span>
  );
}

function Ticker({ q, onRemove }) {
  const up = (q.quote?.changePercent ?? 0) >= 0;
  return (
    <Link
      to={`/stock/${q.symbol}`}
      className="group relative block p-5 border-b border-black/10 hover:bg-paper2 transition-colors"
      data-testid={`watch-row-${q.symbol}`}
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono-num font-bold text-xl">{q.symbol}</span>
            <span className="text-xs text-ink2 truncate max-w-[220px]">{q.quote?.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="font-mono-num text-lg">{fmt.money(q.quote?.price)}</div>
            <div className={`font-mono-num text-xs ${up ? "text-pos" : "text-neg"} flex items-center gap-1 justify-end`}>
              {up ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
              {fmt.pct(q.quote?.changePercent)}
            </div>
          </div>
          <button
            onClick={(e) => { e.preventDefault(); onRemove(q.symbol); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-ink2 hover:text-neg"
            data-testid={`watch-remove-${q.symbol}`}
            aria-label={`Remove ${q.symbol}`}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [watchlist, setWatchlist] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [w, p] = await Promise.all([api.get("/watchlist"), api.get("/portfolio")]);
      setWatchlist(w.data);
      setPortfolio(p.data);
    } catch (e) {
      if (e.response?.status === 401) nav("/", { replace: true });
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/stocks/search`, { params: { q } });
        const base = r.data.results;
        // Enrich each result with live quote + net margin
        const enriched = await Promise.all(base.map(async (item) => {
          try {
            const [q1, o1] = await Promise.all([
              api.get(`/stocks/quote/${item.symbol}`),
              api.get(`/stocks/overview/${item.symbol}`),
            ]);
            return { ...item, quote: q1.data, overview: o1.data };
          } catch {
            return item;
          }
        }));
        setResults(enriched);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const addSymbol = async (symbol) => {
    setAdding(true);
    try {
      await api.post("/watchlist", { symbol });
      toast.success(`${symbol} added to watchlist`);
      setQ(""); setResults([]);
      load();
    } catch { toast.error("Failed to add"); }
    finally { setAdding(false); }
  };
  const remove = async (symbol) => {
    await api.delete(`/watchlist/${symbol}`);
    load();
  };

  const s = portfolio?.summary;
  const plUp = (s?.total_pl ?? 0) >= 0;

  return (
    <div className="min-h-screen bg-paper">
      <Nav />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="label-eyebrow">Dashboard</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-1">Your market view.</h1>
          </div>
          <div className="text-right hidden md:block">
            <div className="label-eyebrow">Portfolio value</div>
            <div className="font-mono-num text-3xl mt-1">{fmt.money(s?.total_value ?? 0)}</div>
            <div className={`font-mono-num text-sm ${plUp ? "text-pos" : "text-neg"}`}>
              {fmt.money(s?.total_pl ?? 0)} · {fmt.pct(s?.total_pl_pct ?? 0)}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-0 border border-black/10">
          {/* Portfolio KPI cards */}
          <div className="p-6 border-b lg:border-b-0 lg:border-r border-black/10 bg-ikb text-paper">
            <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.7)" }}>Total invested</div>
            <div className="font-mono-num text-4xl mt-2">{fmt.money(s?.total_cost ?? 0)}</div>
            <div className="text-xs mt-2 opacity-80">{portfolio?.holdings?.length ?? 0} holdings</div>
          </div>
          <div className="p-6 border-b lg:border-b-0 lg:border-r border-black/10 bg-coral text-paper">
            <div className="label-eyebrow" style={{ color: "rgba(255,255,255,0.8)" }}>Unrealized P&amp;L</div>
            <div className="font-mono-num text-4xl mt-2">{fmt.money(s?.total_pl ?? 0)}</div>
            <div className="text-xs mt-2 opacity-90">{fmt.pct(s?.total_pl_pct ?? 0)} lifetime</div>
          </div>
          <div className="p-6 bg-paper">
            <div className="label-eyebrow">Watchlist</div>
            <div className="font-mono-num text-4xl mt-2">{watchlist.length}</div>
            <Link to="/portfolio" className="text-xs mt-2 inline-flex items-center gap-1 text-ikb hover:underline" data-testid="dashboard-manage-portfolio">
              Manage portfolio <ArrowRight size={12} />
            </Link>
          </div>
        </div>

        {/* Search + Watchlist */}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 border border-black/10">
            <div className="p-4 border-b border-black/10 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Watchlist</div>
                <h2 className="font-display text-2xl mt-1">Tickers you follow</h2>
              </div>
              <span className="font-mono-num text-xs text-ink2">{watchlist.length} SYMBOLS</span>
            </div>
            {watchlist.length === 0 ? (
              <div className="p-10 text-center">
                <p className="text-ink2 text-sm">No tickers yet. Search below to add your first.</p>
              </div>
            ) : (
              <div>
                {watchlist.map((w) => <Ticker key={w.symbol} q={w} onRemove={remove} />)}
              </div>
            )}
          </div>

          <div>
            <div className="border border-black/10">
              <div className="p-4 border-b border-black/10">
                <div className="label-eyebrow">Add a ticker</div>
                <h2 className="font-display text-xl mt-1">Search stocks</h2>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 border border-black/15 bg-paper2 focus-within:border-ikb">
                  <MagnifyingGlass size={16} className="ml-3 text-ink2" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="AAPL, Tesla, NVDA…"
                    className="flex-1 px-2 py-3 bg-transparent focus:outline-none text-sm"
                    data-testid="dashboard-search-input"
                  />
                </div>
                {results.length > 0 && (
                  <ul className="mt-3 border border-black/10 divide-y divide-black/10 max-h-[400px] overflow-y-auto">
                    {results.map((r) => {
                      const up = (r.quote?.changePercent ?? 0) >= 0;
                      return (
                        <li key={r.symbol} className="p-3 hover:bg-paper2" data-testid={`search-result-${r.symbol}`}>
                          <div className="flex items-start justify-between gap-3">
                            <button
                              onClick={() => nav(`/stock/${r.symbol}`)}
                              className="text-left flex-1"
                              data-testid={`search-open-${r.symbol}`}
                            >
                              <div className="flex items-baseline gap-2">
                                <span className="font-mono-num font-bold text-sm">{r.symbol}</span>
                                <span className="text-xs text-ink2 truncate max-w-[200px]">{r.name}</span>
                              </div>
                              {r.quote && (
                                <div className="mt-1 flex items-center gap-3 text-xs font-mono-num">
                                  <span>{fmt.money(r.quote.price)}</span>
                                  <span className={up ? "text-pos" : "text-neg"}>{fmt.pct(r.quote.changePercent)}</span>
                                </div>
                              )}
                              {r.overview && (
                                <div className="mt-2 flex items-center gap-3 text-[11px] font-mono-num">
                                  <MarginPill label="G" value={r.overview.grossMargin} color="#002FA7" />
                                  <MarginPill label="O" value={r.overview.operatingMargin} color="#3B82F6" />
                                  <MarginPill label="N" value={r.overview.netMargin} color="#93C5FD" />
                                </div>
                              )}
                            </button>
                            <button
                              onClick={() => addSymbol(r.symbol)}
                              disabled={adding}
                              className="btn-primary text-xs shrink-0"
                              data-testid={`search-add-${r.symbol}`}
                            >
                              <Plus size={12} /> Add
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="mt-4">
                  <div className="label-eyebrow mb-2">Popular</div>
                  <div className="flex flex-wrap gap-2">
                    {["AAPL","MSFT","GOOGL","NVDA","TSLA","META","AMZN","NFLX"].map((s) => (
                      <button
                        key={s}
                        onClick={() => addSymbol(s)}
                        className="px-3 py-1.5 border border-black/15 text-xs font-mono-num hover:bg-ink hover:text-paper transition-colors"
                        data-testid={`quick-add-${s}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
